import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../services/apiClient';

// Executive page → Fieldwire Setup (Ryan, Sep 28 2026): every active job QuickBooks
// Time knows, whether it has a Fieldwire project, and one button to make the missing
// ones "the same as 2631" — the project (number as its code), the crew, the categories.
// Nothing is made until the button is pressed and confirmed.
export default function FieldwireSetupPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [chosen, setChosen] = useState({});
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [imp, setImp] = useState(null);          // last import: { seen, matched }
  const [importing, setImporting] = useState(false);
  const [pick, setPick] = useState({});           // fw_project_id → job id chosen for a hand match
  const [linking, setLinking] = useState('');
  const [showOld, setShowOld] = useState(false);  // the 1xxx-era projects, folded by default
  // While the background pull runs, look again every few seconds.
  const pulling = !!data?.pulling?.running;
  useEffect(() => { if (!pulling) return undefined; const t = setInterval(() => { apiFetch('/fieldwire/setup-active').then(d => setData(d)).catch(() => {}); }, 6000); return () => clearInterval(t); }, [pulling]);
  // Worth a hand match = no number at all, or a current-era number (26xx / 27xx, WO26… /
  // WO27…) that simply did not line up. Anything numbered from earlier years is a finished
  // job the app never had (the real account holds 300 of them) and folds away.
  const tokenOf = u => { const m = String(u.code || u.name || '').match(/^\s*(?:WO\s*)?0*(\d{3,6})/i); return m ? m[1] : null; };
  const isOld = u => { const t = tokenOf(u); return !!t && !/^2[67]/.test(t); };

  async function importAll() {
    if (importing) return;
    setImporting(true); setErr('');
    try {
      const r = await apiFetch('/fieldwire/import', { method: 'POST', body: JSON.stringify({}) });
      setImp(r);
      setData(d => ({ ...d, jobs: r.jobs || d.jobs, unmatched: r.unmatched || [] }));
      setChosen(Object.fromEntries((r.jobs || []).filter(j => !j.linked).map(j => [j.id, true])));
    } catch (e) { setErr(e?.error || 'Could not read Fieldwire.'); }
    setImporting(false);
  }
  async function linkOne(fwId) {
    const jobId = pick[fwId];
    if (!jobId || linking) return;
    setLinking(fwId); setErr('');
    try {
      await apiFetch(`/projects/${jobId}/fieldwire/link`, { method: 'PUT', body: JSON.stringify({ fw_project_id: fwId }) });
      await load();
    } catch (e) { setErr(e?.error || 'Could not match that project.'); }
    setLinking('');
  }

  const load = () => apiFetch('/fieldwire/setup-active')
    .then(d => { setData(d); setChosen(Object.fromEntries((d?.jobs || []).filter(j => !j.linked).map(j => [j.id, true]))); })
    .catch(e => setErr(e?.error || 'Could not read the jobs.'));
  useEffect(() => { load(); }, []);

  if (err) return <div style={{ padding: 20, color: '#b91c1c' }}>{err}</div>;
  if (!data) return <div style={{ padding: 20, color: '#6b7280' }}>Reading the jobs…</div>;
  if (!data.configured) return <div style={{ padding: 20, color: '#6b7280' }}>Fieldwire isn't connected on the server yet.</div>;
  const jobs = data.jobs || [];
  const linked = jobs.filter(j => j.linked), missing = jobs.filter(j => !j.linked);
  const n = missing.filter(j => chosen[j.id]).length;

  async function run() {
    const ids = missing.filter(j => chosen[j.id]).map(j => j.id);
    if (!ids.length || busy) return;
    const src = data.source ? ` Each gets the ${data.source.people} people and ${data.source.categories.length} categories of ${data.source.name}.` : '';
    if (!window.confirm(`Make ${ids.length} Fieldwire project${ids.length === 1 ? '' : 's'}?${src}`)) return;
    setBusy(true); setErr('');
    try {
      const r = await apiFetch('/fieldwire/setup-active', { method: 'POST', body: JSON.stringify({ project_ids: ids }) });
      setResults(r.results || []);
      setData(d => ({ ...d, jobs: r.jobs || d.jobs }));
      setChosen({});
    } catch (e) { setErr(e?.error || 'Could not make the projects.'); }
    setBusy(false);
  }

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>📐 Fieldwire Setup</div>
        <div style={{ fontSize: 13, color: '#6b7280' }}>{jobs.length} active job{jobs.length === 1 ? '' : 's'} QuickBooks Time knows · {linked.length} in Fieldwire · {missing.length} without a project</div>
        <span style={{ flex: 1 }} />
        <button onClick={importAll} disabled={importing} title="Read every project the key can see in Fieldwire and match each to a job by the number in its code or name"
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #99f6e4', background: '#f0fdfa', color: '#0f766e', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
          {importing ? 'Reading Fieldwire…' : '⇩ Import from Fieldwire'}
        </button>
      </div>
      {imp && (
        <div style={{ fontSize: 13, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', color: '#166534' }}>
          Fieldwire shows {imp.seen} project{imp.seen === 1 ? '' : 's'} to this key · {imp.matched.length} matched to a job{imp.matched.filter(m => m.new).length ? ` (${imp.matched.filter(m => m.new).length} new)` : ''} · {(imp.unmatched || []).length} not matched — pick the job for each below.
        </div>
      )}
      {data.pulling && (data.pulling.running || data.pulling.finishedAt) && (
        <div style={{ fontSize: 12.5, color: data.pulling.running ? '#92400e' : '#166534' }}>
          {data.pulling.running ? `⏳ Pulling the newly matched projects from Fieldwire… ${data.pulling.done} of ${data.pulling.total}` : `✓ Pulled ${data.pulling.done} project${data.pulling.done === 1 ? '' : 's'}${data.pulling.failed ? ` (${data.pulling.failed} did not answer)` : ''}`}
        </div>
      )}
      {(data.unmatched || []).length > 0 && (
        <div style={{ border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 10, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', gap: 10, alignItems: 'center' }}>
            In Fieldwire, not matched to a job ({data.unmatched.filter(u => !isOld(u)).length})
            {data.unmatched.some(isOld) && (
              <button onClick={() => setShowOld(v => !v)} style={{ background: 'none', border: '1px solid #fcd34d', borderRadius: 999, padding: '2px 9px', fontSize: 11, fontWeight: 700, color: '#92400e', cursor: 'pointer', textTransform: 'none', letterSpacing: 0 }}>
                {showOld ? 'Hide' : 'Show'} {data.unmatched.filter(isOld).length} projects from earlier years
              </button>
            )}
          </div>
          {data.unmatched.filter(u => showOld || !isOld(u)).map(u => (
            <div key={u.fw_project_id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', minWidth: 220 }}>{u.code ? `${u.code} · ` : ''}{u.name}</span>
              <select value={pick[u.fw_project_id] || ''} onChange={e => setPick(p => ({ ...p, [u.fw_project_id]: e.target.value }))}
                style={{ flex: 1, minWidth: 240, border: '1px solid #fcd34d', borderRadius: 7, padding: '6px 8px', fontSize: 12.5, background: '#fff' }}>
                <option value="">— which job is this? —</option>
                {(data.all_jobs || []).map(j => <option key={j.id} value={j.id}>{j.project_number} · {j.name}</option>)}
              </select>
              <button onClick={() => linkOne(u.fw_project_id)} disabled={!pick[u.fw_project_id] || !!linking}
                style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: pick[u.fw_project_id] ? '#b45309' : '#fde68a', color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: pick[u.fw_project_id] ? 'pointer' : 'default' }}>
                {linking === u.fw_project_id ? 'Matching…' : 'Match'}
              </button>
            </div>
          ))}
        </div>
      )}
      {data.source && (
        <div style={{ fontSize: 12.5, color: '#0f766e', background: '#f0fdfa', border: '1px solid #ccfbf1', borderRadius: 8, padding: '8px 12px' }}>
          New projects copy <b>{data.source.name}</b>: {data.source.people} people on the roster and the categories {data.source.categories.join(', ') || '—'}. The job number becomes the project code.
        </div>
      )}
      {missing.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={run} disabled={!n || busy}
            style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: n && !busy ? '#0f766e' : '#a7f3d0', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: n && !busy ? 'pointer' : 'default' }}>
            {busy ? 'Making them… this takes a while' : `Set up ${n} job${n === 1 ? '' : 's'} in Fieldwire`}
          </button>
          <button onClick={() => setChosen(Object.fromEntries(missing.map(j => [j.id, true])))} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 11px', fontSize: 12, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>All</button>
          <button onClick={() => setChosen({})} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 11px', fontSize: 12, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>None</button>
        </div>
      )}
      {results && (
        <div style={{ fontSize: 13, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ fontWeight: 800, color: '#166534' }}>✓ {results.filter(r => r.ok).length} made{results.some(r => !r.ok) ? ` · ${results.filter(r => !r.ok).length} could not be made` : ''}</div>
          {results.map(r => (
            <div key={r.id} style={{ color: r.ok ? '#166534' : '#b91c1c' }}>
              {r.project_number}: {r.ok ? `${r.people} people, ${r.categories} categories${r.failed?.length ? ` (${r.failed.length} did not take)` : ''}` : r.error}
            </div>
          ))}
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            {['', 'Job', 'Name', 'Fieldwire'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {jobs.map(j => (
            <tr key={j.id} style={{ borderTop: '1px solid #f3f4f6', opacity: j.linked ? 0.75 : 1 }}>
              <td style={{ padding: '7px 10px', width: 28 }}>{!j.linked && <input type="checkbox" checked={!!chosen[j.id]} onChange={e => setChosen(c => ({ ...c, [j.id]: e.target.checked }))} disabled={busy} />}</td>
              <td style={{ padding: '7px 10px', fontWeight: 800, color: '#e87722', whiteSpace: 'nowrap' }}>{j.project_number}</td>
              <td style={{ padding: '7px 10px', color: '#111827', maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.name}</td>
              <td style={{ padding: '7px 10px', whiteSpace: 'nowrap', color: j.linked ? '#15803d' : '#92400e', fontWeight: 700 }}>{j.linked ? `✓ ${j.fw_name || 'linked'}` : 'not yet'}</td>
            </tr>
          ))}
          {jobs.length === 0 && <tr><td colSpan={4} style={{ padding: 20, color: '#9ca3af', textAlign: 'center' }}>No active job matches a QuickBooks Time jobcode right now.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
